import { Directive, OnDestroy, OnInit, output, input } from "@angular/core";
import { MatAutocomplete } from "@angular/material/autocomplete";
import { Subject, tap, takeUntil, delay } from "rxjs";

export interface IAutoCompleteScrollEvent {
  autoComplete: MatAutocomplete;
  scrollEvent: Event;
}

@Directive({
  selector: "[appTypeAheadScroll]",
  providers: [MatAutocomplete]
})
export class TypeAheadScrollDirective implements OnInit, OnDestroy {
  autoComplete = input<MatAutocomplete>();
  thresholdPercent = input(0.95);
  optionsScroll = output<IAutoCompleteScrollEvent>();

  private _onDestroy = new Subject();

  ngOnInit(): void {
    const autoComplete = this.autoComplete();
    if (!autoComplete) {
      return;
    }

    autoComplete.opened
      .pipe(
        // When autocomplete raises opened, panel is not yet created (by Overlay)
        // The panel will be available on next tick
        // The panel wil NOT open if there are no options to display
        delay(150),
        tap(() => {
          // Remove listener just for safety, in case the close event is skipped.
          this._removeScrollEventListener();
          this._addScrollEventListener();
        }),
        takeUntil(this._onDestroy)
      )
      .subscribe();

    autoComplete.closed
      .pipe(
        tap(() => this._removeScrollEventListener()),
        takeUntil(this._onDestroy)
      )
      .subscribe();
  }

  ngOnDestroy() {
    this._onDestroy.next(null);
    this._onDestroy.complete();
    this._removeScrollEventListener();
  }

  onScroll = (event: Event) => {
    const thresholdPercent = this.thresholdPercent();
    if (thresholdPercent === undefined) {
      this.optionsScroll.emit({ autoComplete: this.autoComplete(), scrollEvent: event });
    } else {
      const threshold = (thresholdPercent * 100 * (<HTMLInputElement>event.target).scrollHeight) / 100;
      const current = (<HTMLInputElement>event.target).scrollTop + (<HTMLInputElement>event.target).clientHeight;

      if (current > threshold) {
        this.optionsScroll.emit({ autoComplete: this.autoComplete(), scrollEvent: event });
      }
    }
  };

  private _addScrollEventListener() {
    this.autoComplete()?.panel?.nativeElement?.addEventListener("scroll", this.onScroll.bind(this));
  }

  private _removeScrollEventListener() {
    this.autoComplete()?.panel?.nativeElement?.removeEventListener("scroll", this.onScroll);
  }
}
